import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { DatePicker } from '../components/ui/date-picker';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import { toast } from 'sonner';
import { ArrowLeftIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { formatCurrencyAR } from '@/utils/currencyFormatter';
import { hoyLocal, toYMD } from '../utils/fechaLocal';

/* ────────── types ────────── */

type Supply = {
  id: string;
  name: string;
  description?: string;
  category?: string;
  unit: string;
  stock: number;
  minStock: number;
  location?: string;
  costPrice: number;
  supplier?: string;
  attachmentUrl?: string;
  isActive: number;
};

type Movement = {
  id: string;
  type: 'in' | 'out' | 'adjust';
  quantity: number;
  unitCost?: number;
  totalCost?: number;
  reason?: string;
  notes?: string;
  performedBy?: string;
  serviceName?: string;
  sourceType?: string;
  sourceId?: string;
  createdAt: string;
};

type OverviewStats = {
  avgDaily: number;
  avgWeekly: number;
  projectedDays: number | null;
  lastOutAt?: string | null;
  abnormalConsumption: boolean;
  critical: boolean;
  byService: { serviceName: string; totalQty: number; totalCost: number }[];
};

type ConsumptionPoint = { day: string; totalQty: number };
type ServiceBreakdown = { serviceName: string; totalQty: number; totalCost: number };

type EfficiencySupply = {
  supplyName: string;
  supplyUnit: string;
  totalConsumed: number;
  totalServices: number;
  yield: number;
  costPerService: number;
  linkedServices: { serviceName: string; count: number }[];
};

type EfficiencyMonthlyPoint = { month: string; services: number; consumed: number; yield: number };
type EfficiencyRequesterOption = { id: string; name: string; requestCount: number };
type EfficiencyAreaOption = { id: string; name: string; requestCount: number };

type EfficiencyData = {
  supplies: EfficiencySupply[];
  summary: { totalServices: number; totalConsumed: number; avgYield: number; avgCostPerService: number };
  monthlyTrend: EfficiencyMonthlyPoint[];
  filters?: {
    requesters: EfficiencyRequesterOption[];
    areas: EfficiencyAreaOption[];
  };
} | null;

type KpiCardData = { label: string; value: string; hint: string };

/* ────────── helpers ────────── */

const fmtNum = (v: number) => v.toLocaleString('es-AR', { maximumFractionDigits: 2 });
const fmtMoney = (v: number) => formatCurrencyAR(v);
const toIsoDate = (value: Date) => toYMD(value);
const getTodayDate = () => toIsoDate(new Date());
const getOneYearAgoDate = () => {
  const value = new Date();
  value.setFullYear(value.getFullYear() - 1);
  return toIsoDate(value);
};
const getCurrentMonthValue = () => hoyLocal().slice(0, 7);
const buildMonthRange = (value: string) => {
  if (!value) return { startDate: '', endDate: '' };
  const [year, month] = value.split('-').map(Number);
  if (!year || !month) return { startDate: '', endDate: '' };
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
};
const formatMonthLabel = (value: string) => {
  if (!value) return '';
  const [year, month] = value.split('-').map(Number);
  if (!year || !month) return value;
  return new Date(year, month - 1, 1).toLocaleDateString('es-AR', { month: 'short', year: '2-digit' });
};
const round2 = (value: number) => Math.round((value || 0) * 100) / 100;

const movTypeLabel: Record<string, string> = { in: 'Entrada', out: 'Salida', adjust: 'Ajuste' };
const movTypeColor: Record<string, string> = {
  in: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  out: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  adjust: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
};

/* ────────── component ────────── */

const SupplyView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  /* ── core data ── */
  const [loading, setLoading] = useState(true);
  const [supply, setSupply] = useState<Supply | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [stats, setStats] = useState<OverviewStats | null>(null);

  /* ── analytics ── */
  const [rangeDays, setRangeDays] = useState<30 | 60 | 90>(90);
  const [rangeMode, setRangeMode] = useState<'preset' | 'custom'>('preset');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [consumptionSeries, setConsumptionSeries] = useState<ConsumptionPoint[]>([]);
  const [byService, setByService] = useState<ServiceBreakdown[]>([]);
  const [totals, setTotals] = useState<{ totalConsumed: number; totalCost: number }>({ totalConsumed: 0, totalCost: 0 });

  /* ── efficiency ── */
  const [efficiency, setEfficiency] = useState<EfficiencyData>(null);
  const [efficiencyLoading, setEfficiencyLoading] = useState(false);
  const [efficiencyRangeMode, setEfficiencyRangeMode] = useState<'rolling' | 'month' | 'custom'>('rolling');
  const [efficiencyMonth, setEfficiencyMonth] = useState(getCurrentMonthValue());
  const [efficiencyFrom, setEfficiencyFrom] = useState(getOneYearAgoDate());
  const [efficiencyTo, setEfficiencyTo] = useState(getTodayDate());
  const [efficiencyScope, setEfficiencyScope] = useState<'all' | 'employee' | 'area'>('all');
  const [efficiencyRequesterId, setEfficiencyRequesterId] = useState('all');
  const [efficiencyAreaId, setEfficiencyAreaId] = useState('all');

  /* ── service links ── */
  const [serviceLinks, setServiceLinks] = useState<{ id: string; serviceName: string; serviceProductId: string }[]>([]);

  /* ────────── fetch overview (on mount) ────────── */
  useEffect(() => {
    if (!id) return;
    const run = async () => {
      setLoading(true);
      try {
        const [overviewRes, linksRes] = await Promise.all([
          axios.get(`/api/supplies/${id}/overview`),
          axios.get(`/api/supplies/${id}/service-links`).catch(() => ({ data: { links: [] } })),
        ]);
        setSupply(overviewRes.data?.supply || null);
        setMovements(overviewRes.data?.movements || []);
        setStats(overviewRes.data?.stats || null);
        setServiceLinks(linksRes.data?.links || []);
      } catch (err) {
        console.error('Error loading supply view:', err);
        toast.error('No se pudo cargar la ficha del insumo');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  /* ────────── fetch analytics (on range change) ────────── */
  const fetchAnalytics = useCallback(async () => {
    if (!id) return;
    try {
      const params: Record<string, string | number> = {};
      if (rangeMode === 'custom' && customFrom && customTo) {
        params.from = customFrom;
        params.to = customTo;
      } else {
        params.days = rangeDays;
      }
      const res = await axios.get(`/api/supplies/${id}/analytics`, { params });
      setConsumptionSeries(res.data?.series || []);
      setByService(res.data?.byService || []);
      setTotals(res.data?.totals || { totalConsumed: 0, totalCost: 0 });
    } catch (err) {
      console.error('Error fetching supply analytics:', err);
    }
  }, [id, rangeMode, rangeDays, customFrom, customTo]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  /* ────────── fetch efficiency ────────── */
  const fetchEfficiency = useCallback(async () => {
    if (!id) return;

    const monthRange = buildMonthRange(efficiencyMonth);
    const startDate = efficiencyRangeMode === 'rolling'
      ? getOneYearAgoDate()
      : efficiencyRangeMode === 'month'
        ? monthRange.startDate
        : efficiencyFrom;
    const endDate = efficiencyRangeMode === 'rolling'
      ? getTodayDate()
      : efficiencyRangeMode === 'month'
        ? monthRange.endDate
        : efficiencyTo;

    if (!startDate || !endDate) return;

    try {
      setEfficiencyLoading(true);
      const params: Record<string, string> = { startDate, endDate, supplyId: id };
      if (efficiencyScope === 'employee' && efficiencyRequesterId !== 'all') {
        params.requestedBy = efficiencyRequesterId;
      }
      if (efficiencyScope === 'area' && efficiencyAreaId !== 'all') {
        params.areaId = efficiencyAreaId;
      }

      const res = await axios.get('/api/supply-efficiency/report', { params });
      setEfficiency(res.data || null);
    } catch (err) {
      console.error('Error fetching supply efficiency:', err);
      setEfficiency(null);
    } finally {
      setEfficiencyLoading(false);
    }
  }, [id, efficiencyRangeMode, efficiencyMonth, efficiencyFrom, efficiencyTo, efficiencyScope, efficiencyRequesterId, efficiencyAreaId]);

  useEffect(() => { fetchEfficiency(); }, [fetchEfficiency]);

  /* ────────── chart data ────────── */
  const chartData = useMemo(() => {
    return consumptionSeries.map(p => ({
      date: p.day.slice(5), // MM-DD
      fullDate: p.day,
      value: Number(p.totalQty || 0),
    }));
  }, [consumptionSeries]);

  const consumptionTrend = useMemo(() => {
    if (chartData.length < 2) return null;
    const first = chartData[0].value;
    const last = chartData[chartData.length - 1].value;
    if (first === 0) return null;
    return Math.round(((last - first) / first) * 100);
  }, [chartData]);

  /* ────────── kpi cards ────────── */
  const kpis = useMemo<KpiCardData[]>(() => {
    if (!stats || !supply) return [];

    const costPerMonth = stats.avgDaily * 30 * supply.costPrice;
    const effSupply = efficiency?.supplies?.[0];

    const cards: KpiCardData[] = [
      {
        label: 'Consumo diario',
        value: fmtNum(stats.avgDaily),
        hint: `Prom. ultimos 30 dias (${supply.unit})`
      },
      {
        label: 'Consumo semanal',
        value: fmtNum(stats.avgWeekly),
        hint: `Prom. ultimos 7 dias (${supply.unit})`
      },
      {
        label: 'Cobertura stock',
        value: stats.projectedDays != null ? `${stats.projectedDays} dias` : '-',
        hint: 'Dias estimados hasta agotar stock'
      },
      {
        label: 'Costo mensual est.',
        value: fmtMoney(costPerMonth),
        hint: 'Consumo diario x 30 x costo unitario'
      },
    ];

    if (effSupply) {
      cards.push({
        label: 'Rendimiento',
        value: `${fmtNum(effSupply.yield)} serv/${supply.unit}`,
        hint: 'Servicios realizados por unidad consumida'
      });
    }

    return cards;
  }, [stats, supply, efficiency]);

  /* ────────── service bar chart data ────────── */
  const serviceChartData = useMemo(() => {
    return byService.slice(0, 8).map(s => ({
      name: s.serviceName.length > 18 ? s.serviceName.slice(0, 16) + '...' : s.serviceName,
      fullName: s.serviceName,
      qty: Number(s.totalQty || 0),
      cost: Number(s.totalCost || 0),
    }));
  }, [byService]);

  const efficiencyChartData = useMemo(() => {
    return (efficiency?.monthlyTrend || []).map((point) => ({
      ...point,
      label: formatMonthLabel(point.month),
    }));
  }, [efficiency]);

  const efficiencyThreshold = useMemo(() => {
    if (!efficiency?.monthlyTrend?.length) return 0;
    const baseline = efficiency.summary?.avgYield
      || Math.max(...efficiency.monthlyTrend.map((point) => Number(point.yield) || 0), 0);
    return baseline > 0 ? round2(baseline * 0.8) : 0;
  }, [efficiency]);

  const efficiencyScopeLabel = useMemo(() => {
    if (!efficiency?.filters) return '';
    if (efficiencyScope === 'employee' && efficiencyRequesterId !== 'all') {
      return efficiency.filters.requesters.find((row) => row.id === efficiencyRequesterId)?.name || '';
    }
    if (efficiencyScope === 'area' && efficiencyAreaId !== 'all') {
      return efficiency.filters.areas.find((row) => row.id === efficiencyAreaId)?.name || '';
    }
    return '';
  }, [efficiency, efficiencyScope, efficiencyRequesterId, efficiencyAreaId]);

  const efficiencyPeriodLabel = useMemo(() => {
    if (efficiencyRangeMode === 'rolling') return 'Ultimos 12 meses';
    if (efficiencyRangeMode === 'month') {
      return efficiencyMonth ? `Mes: ${formatMonthLabel(efficiencyMonth)}` : 'Mes puntual';
    }
    if (!efficiencyFrom || !efficiencyTo) return 'Periodo personalizado';
    return `${new Date(`${efficiencyFrom}T00:00:00`).toLocaleDateString('es-AR')} al ${new Date(`${efficiencyTo}T00:00:00`).toLocaleDateString('es-AR')}`;
  }, [efficiencyRangeMode, efficiencyMonth, efficiencyFrom, efficiencyTo]);

  const primaryEfficiencySupply = efficiency?.supplies?.[0] || null;
  const primaryLinkedServices = primaryEfficiencySupply?.linkedServices || [];

  /* ────────── render ────────── */

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!supply) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
        <p className="text-muted-foreground">Insumo no encontrado</p>
        <Button variant="outline" onClick={() => navigate('/supplies')}>Volver a insumos</Button>
      </div>
    );
  }

  const stockValue = supply.stock * supply.costPrice;
  const isLowStock = supply.stock > 0 && supply.stock <= supply.minStock;
  const isOutOfStock = supply.stock <= 0;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      {/* ══════ HEADER ══════ */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/supplies')}>
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> Volver
          </Button>
          <div>
            <h1 className="text-xl font-bold md:text-2xl">{supply.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {supply.category && <Badge variant="secondary">{supply.category}</Badge>}
              {supply.isActive ? <Badge variant="outline" className="text-emerald-600">Activo</Badge>
                : <Badge variant="destructive">Inactivo</Badge>}
              {isOutOfStock && <Badge variant="destructive">Sin stock</Badge>}
              {isLowStock && !isOutOfStock && <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">Stock bajo</Badge>}
              {stats?.abnormalConsumption && <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300">Consumo anormal</Badge>}
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(`/supplies/${id}/edit`)}>
          <PencilSquareIcon className="h-4 w-4 mr-1" /> Editar
        </Button>
      </div>

      {/* ══════ IDENTITY CARD ══════ */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* info general */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Informacion general</h3>
              <div className="grid grid-cols-2 gap-3">
                <InfoItem label="Categoria" value={supply.category || '-'} />
                <InfoItem label="Unidad" value={supply.unit || '-'} />
                <InfoItem label="Proveedor" value={supply.supplier || '-'} />
                <InfoItem label="Ubicacion" value={supply.location || '-'} />
              </div>
              {supply.description && (
                <div>
                  <span className="text-xs text-muted-foreground">Descripcion</span>
                  <p className="text-sm">{supply.description}</p>
                </div>
              )}
            </div>
            {/* comercial */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Comercial</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <InfoItem label="Costo unitario" value={fmtMoney(supply.costPrice)} />
                <InfoItem label="Stock" value={`${fmtNum(supply.stock)} ${supply.unit}`} highlight={isOutOfStock ? 'destructive' : isLowStock ? 'warning' : undefined} />
                <InfoItem label="Stock minimo" value={`${fmtNum(supply.minStock)} ${supply.unit}`} />
                <InfoItem label="Valor en stock" value={fmtMoney(stockValue)} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ══════ KPI CARDS ══════ */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {kpis.map((k, i) => (
            <Card key={i}>
              <CardContent className="py-4 px-4">
                <p className="text-xs text-muted-foreground">{k.label}</p>
                <p className="text-xl font-bold tabular-nums mt-1">{k.value}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{k.hint}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ══════ CONSUMPTION CHART ══════ */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base">Consumo en el periodo</CardTitle>
            <div className="inline-flex items-center gap-1 rounded-lg border p-1">
              {([30, 60, 90] as const).map((d) => (
                <Button
                  key={d}
                  type="button"
                  variant={rangeMode === 'preset' && rangeDays === d ? 'default' : 'ghost'}
                  className="h-8 px-3"
                  onClick={() => { setRangeMode('preset'); setRangeDays(d); }}
                >
                  {d}d
                </Button>
              ))}
              <Button
                type="button"
                variant={rangeMode === 'custom' ? 'default' : 'ghost'}
                className="h-8 px-3"
                onClick={() => setRangeMode('custom')}
              >
                Fechas
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {rangeMode === 'custom' && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 mb-4">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Desde</label>
                <DatePicker value={customFrom} onChange={setCustomFrom}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Hasta</label>
                <DatePicker value={customTo} onChange={setCustomTo}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
              </div>
            </div>
          )}

          {chartData.length > 0 ? (
            <>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 12, right: 16, left: 4, bottom: 8 }}>
                    <defs>
                      <linearGradient id="consumptionGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} />
                    <YAxis tickLine={false} axisLine={false} width={56}
                      tickFormatter={(v) => fmtNum(v)} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="rounded-lg border bg-background p-2 shadow-sm text-sm">
                            <p className="font-medium">{d.fullDate}</p>
                            <p className="text-muted-foreground">Consumo: <span className="font-semibold text-foreground">{fmtNum(d.value)} {supply?.unit}</span></p>
                          </div>
                        );
                      }}
                      cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeOpacity: 0.35, strokeDasharray: '3 3' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="hsl(var(--chart-1))"
                      strokeWidth={2.5}
                      fill="url(#consumptionGrad)"
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 1 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="border-t pt-3 mt-2 flex flex-wrap gap-6 text-sm">
                <div>
                  <span className="text-muted-foreground">Total consumido: </span>
                  <span className="font-semibold">{fmtNum(totals.totalConsumed)} {supply.unit}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Costo total: </span>
                  <span className="font-semibold">{fmtMoney(totals.totalCost)}</span>
                </div>
                {consumptionTrend != null && (
                  <div>
                    <span className="text-muted-foreground">Tendencia: </span>
                    <span className={`font-semibold ${consumptionTrend > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {consumptionTrend > 0 ? '+' : ''}{consumptionTrend}%
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-center text-muted-foreground py-10">Sin datos de consumo en este periodo</p>
          )}
        </CardContent>
      </Card>

      {/* ══════ PROYECCIÓN DE STOCK / REPOSICIÓN ══════ */}
      {supply && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Proyección de stock</CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const stock = Number(supply.stock || 0);
              const unit = supply.unit || 'u';
              const minStock = Number(supply.minStock || 0);
              const avgDaily = Number(stats?.avgDaily || 0);
              const projDays = stats?.projectedDays ?? null;
              const hasConsumo = avgDaily > 0 && projDays != null;
              const reorderDate = hasConsumo ? new Date(Date.now() + (projDays as number) * 86400000) : null;
              const urgente = stock <= 0 || (minStock > 0 && stock <= minStock) || (hasConsumo && (projDays as number) <= 7);
              return (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Stock actual</p>
                      <p className="text-xl font-semibold">{fmtNum(stock)} <span className="text-sm font-normal text-muted-foreground">{unit}</span></p>
                      {minStock > 0 && <p className="text-xs text-muted-foreground">Mínimo: {fmtNum(minStock)}</p>}
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Consumo promedio</p>
                      <p className="text-xl font-semibold">{fmtNum(avgDaily * 7)} <span className="text-sm font-normal text-muted-foreground">/ sem</span></p>
                      <p className="text-xs text-muted-foreground">{fmtNum(avgDaily)} / día {stats?.abnormalConsumption ? '· subió esta semana' : ''}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">Se agota en</p>
                      <p className="text-xl font-semibold">{hasConsumo ? `${fmtNum(projDays as number)} días` : '—'}</p>
                      {!hasConsumo && <p className="text-xs text-muted-foreground">sin consumo reciente</p>}
                    </div>
                    <div className={`rounded-lg border p-3 ${urgente ? 'border-red-500/40 bg-red-500/5' : ''}`}>
                      <p className="text-xs text-muted-foreground">Reponer</p>
                      <p className={`text-xl font-semibold ${urgente ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {urgente ? 'Ahora' : (reorderDate ? reorderDate.toLocaleDateString('es-AR') : '—')}
                      </p>
                      {!urgente && reorderDate && <p className="text-xs text-muted-foreground">fecha estimada</p>}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {stock <= 0
                      ? 'Sin stock: reponé para poder seguir usándolo.'
                      : hasConsumo
                        ? `Al ritmo actual (${fmtNum(avgDaily)} ${unit}/día) el stock alcanza ~${fmtNum(projDays as number)} días${minStock > 0 ? `; el mínimo configurado es ${fmtNum(minStock)} ${unit}` : ''}.`
                        : 'No hay consumo suficiente en el período para proyectar cuándo reponer.'}
                  </p>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ══════ LINKED SERVICES / EFFICIENCY ══════ */}
      {serviceLinks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Servicios vinculados y eficiencia</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Periodo</label>
                  <select
                    value={efficiencyRangeMode}
                    onChange={(e) => {
                      const value = e.target.value as 'rolling' | 'month' | 'custom';
                      setEfficiencyRangeMode(value);
                      if (value === 'rolling') {
                        setEfficiencyFrom(getOneYearAgoDate());
                        setEfficiencyTo(getTodayDate());
                      }
                    }}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="rolling">Ultimos 12 meses</option>
                    <option value="month">Mes puntual</option>
                    <option value="custom">Entre fechas</option>
                  </select>
                </div>

                {efficiencyRangeMode === 'month' && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Mes</label>
                    <input
                      type="month"
                      value={efficiencyMonth}
                      onChange={(e) => setEfficiencyMonth(e.target.value)}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    />
                  </div>
                )}

                {efficiencyRangeMode === 'custom' && (
                  <>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Desde</label>
                      <DatePicker
                        value={efficiencyFrom}
                        onChange={setEfficiencyFrom}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Hasta</label>
                      <DatePicker
                        value={efficiencyTo}
                        onChange={setEfficiencyTo}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Segmentar por</label>
                  <select
                    value={efficiencyScope}
                    onChange={(e) => {
                      const value = e.target.value as 'all' | 'employee' | 'area';
                      setEfficiencyScope(value);
                      if (value !== 'employee') setEfficiencyRequesterId('all');
                      if (value !== 'area') setEfficiencyAreaId('all');
                    }}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="all">Todos</option>
                    <option value="employee">Empleado</option>
                    <option value="area">Sector</option>
                  </select>
                </div>

                {efficiencyScope === 'employee' && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Empleado</label>
                    <select
                      value={efficiencyRequesterId}
                      onChange={(e) => setEfficiencyRequesterId(e.target.value)}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="all">Todos los empleados</option>
                      {(efficiency?.filters?.requesters || []).map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.name} ({row.requestCount})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {efficiencyScope === 'area' && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Sector</label>
                    <select
                      value={efficiencyAreaId}
                      onChange={(e) => setEfficiencyAreaId(e.target.value)}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="all">Todos los sectores</option>
                      {(efficiency?.filters?.areas || []).map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.name} ({row.requestCount})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{efficiencyPeriodLabel}</span>
                {efficiencyScope !== 'all' && efficiencyScopeLabel && (
                  <span>· {efficiencyScope === 'employee' ? 'Empleado' : 'Sector'}: {efficiencyScopeLabel}</span>
                )}
              </div>

              {efficiencyLoading ? (
                <p className="text-sm text-muted-foreground py-6">Cargando eficiencia del insumo...</p>
              ) : efficiency && efficiency.supplies.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <MiniKpi label="Servicios realizados" value={fmtNum(efficiency.summary.totalServices)} />
                    <MiniKpi label="Insumo consumido" value={`${fmtNum(efficiency.summary.totalConsumed)} ${supply.unit}`} />
                    <MiniKpi label="Rendimiento" value={`${fmtNum(efficiency.summary.avgYield)} serv/${supply.unit}`} />
                    <MiniKpi label="Costo insumo/servicio" value={fmtMoney(efficiency.summary.avgCostPerService)} />
                  </div>

                  {efficiencyChartData.length > 0 ? (
                    <div className="rounded-xl border bg-muted/20 p-4 space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">Rendimiento mes a mes</p>
                          <p className="text-xs text-muted-foreground">
                            Curva de servicios realizados / unidades consumidas para detectar caidas de eficiencia.
                          </p>
                        </div>
                        {efficiencyThreshold > 0 && (
                          <div className="text-xs text-muted-foreground">
                            Umbral minimo esperado:{' '}
                            <span className="font-semibold text-red-600">{fmtNum(efficiencyThreshold)} serv/{supply.unit}</span>
                          </div>
                        )}
                      </div>

                      <div className="h-[280px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={efficiencyChartData} margin={{ top: 12, right: 18, left: 4, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                            <YAxis tickLine={false} axisLine={false} width={56} tickFormatter={(value) => fmtNum(value)} />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const point = payload[0].payload as EfficiencyMonthlyPoint & { label: string };
                                return (
                                  <div className="rounded-lg border bg-background p-2 shadow-sm text-sm">
                                    <p className="font-medium">{point.label}</p>
                                    <p className="text-muted-foreground">
                                      Rendimiento:{' '}
                                      <span className="font-semibold text-foreground">{fmtNum(point.yield)} serv/{supply.unit}</span>
                                    </p>
                                    <p className="text-muted-foreground">
                                      Servicios: <span className="font-semibold text-foreground">{fmtNum(point.services)}</span>
                                    </p>
                                    <p className="text-muted-foreground">
                                      Consumido: <span className="font-semibold text-foreground">{fmtNum(point.consumed)} {supply.unit}</span>
                                    </p>
                                  </div>
                                );
                              }}
                            />
                            {efficiencyThreshold > 0 && (
                              <ReferenceLine
                                y={efficiencyThreshold}
                                stroke="#dc2626"
                                strokeDasharray="6 6"
                                ifOverflow="extendDomain"
                                label={{ value: 'Umbral minimo', position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                              />
                            )}
                            <Line
                              type="monotone"
                              dataKey="yield"
                              stroke="hsl(var(--chart-3))"
                              strokeWidth={3}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5, strokeWidth: 1 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No hay suficiente historial para dibujar el rendimiento mensual en el periodo seleccionado.
                    </p>
                  )}

                  {primaryLinkedServices.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Servicio</TableHead>
                          <TableHead className="text-right">Servicios completados</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {primaryLinkedServices.map((ls, i) => (
                          <TableRow key={i}>
                            <TableCell>{ls.serviceName}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtNum(ls.count)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Servicios vinculados:</p>
                  <div className="flex flex-wrap gap-2">
                    {serviceLinks.map(l => (
                      <Badge key={l.id} variant="secondary">{l.serviceName}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    No hay datos de eficiencia todavia. Se calculan automaticamente cuando haya servicios completados y pedidos entregados.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ══════ MOVEMENT HISTORY ══════ */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial de movimientos</CardTitle>
        </CardHeader>
        <CardContent>
          {movements.length > 0 ? (
            <div className="overflow-auto max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.slice(0, 50).map((m) => (
                    <TableRow key={m.id}>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${movTypeColor[m.type] || ''}`}>
                          {movTypeLabel[m.type] || m.type}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {m.type === 'in' ? '+' : m.type === 'out' ? '-' : ''}{fmtNum(m.quantity)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {m.serviceName ? (
                          (m.sourceType === 'remito' || m.sourceType === 'appointment') ? (
                            <button
                              type="button"
                              onClick={() => {
                                const code = String(m.serviceName).split(' · ')[0].trim();
                                navigate(m.sourceType === 'remito'
                                  ? `/remitos?q=${encodeURIComponent(code)}`
                                  : `/appointments-enhanced?q=${encodeURIComponent(code)}`);
                              }}
                              className="text-primary hover:underline text-left"
                            >
                              {m.serviceName}
                            </button>
                          ) : m.serviceName
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{m.performedBy || '-'}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {m.createdAt ? new Date(m.createdAt).toLocaleDateString('es-AR') : '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[160px] truncate">{m.reason || m.notes || '-'}</TableCell>
                      <TableCell className="text-right tabular-nums">{m.totalCost ? fmtMoney(m.totalCost) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">Sin movimientos registrados</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ── sub-components ── */

function InfoItem({ label, value, highlight }: { label: string; value: string; highlight?: 'destructive' | 'warning' }) {
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <p className={`text-sm font-medium ${
        highlight === 'destructive' ? 'text-red-600' : highlight === 'warning' ? 'text-amber-600' : ''
      }`}>{value}</p>
    </div>
  );
}

function MiniKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3 bg-muted/30">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-bold tabular-nums mt-1">{value}</p>
    </div>
  );
}

export default SupplyView;
